import { uniqueId } from 'lodash-es';
import { useMemo } from 'react';

import { PosIconProps, SvgWrapper } from './SvgWrapper';

const BOLT_PATH =
  'M70.858 11.1519C86.0761 -9.78975 119.193 0.690725 119.592 26.5747L120.195 65.7114H149.21C171.088 65.7117 183.843 90.4099 171.178 108.25L106.794 198.94C91.5979 220.344 57.8839 209.594 57.8834 183.344V144.292H26.9899C4.9753 144.292 -7.74649 119.32 5.19493 101.511L70.858 11.1519ZM108.368 26.7476C108.135 11.6486 88.8163 5.53455 79.9391 17.7505L14.276 108.111L13.9332 108.601C7.07111 118.787 14.1173 132.618 26.3912 133.055L26.9899 133.066H62.3736C66.0933 133.066 69.109 136.082 69.109 139.801V183.344L69.1236 184.055C69.7122 198.613 88.3095 204.543 97.2164 193.013L97.6402 192.442L162.025 101.75C169.297 91.5068 162.201 77.3869 149.804 76.9478L149.21 76.937H115.774L115.431 76.9282C112.024 76.7575 109.277 74.0523 109.053 70.6479L109.039 70.3052L108.368 26.7476ZM86.0318 30.3735C89.4239 25.6225 96.9145 28.0223 96.9147 33.8599V82.6079C96.9147 85.9215 99.6012 88.6077 102.915 88.6079H144.195C149.074 88.6081 151.913 94.123 149.078 98.0942L90.9596 179.499C87.5676 184.25 80.0768 181.85 80.0768 176.012V127.265C80.0768 123.952 77.3903 121.265 74.0768 121.265H32.7965C27.9171 121.265 25.0786 115.75 27.9137 111.779L86.0318 30.3735Z';

export type BoostStrategyIconProps = Omit<
  PosIconProps,
  'viewBox' | 'fill' | 'stroke'
> & { level: 1 | 2 | 3 | 4 | 5 | 6 };

export const BoostStrategyIcon = ({
  level,
  ...props
}: BoostStrategyIconProps) => {
  const ids = useMemo(
    () => ({
      gradient: uniqueId('boost-strategy-gradient-'),
      filter: uniqueId('boost-strategy-filter-'),
      shimmer: uniqueId('boost-strategy-shimmer-'),
    }),
    []
  );

  if (level === 1) {
    return (
      <SvgWrapper
        viewBox="0 0 177 211"
        fill="none"
        {...props}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={BOLT_PATH} fill="none" stroke="#4720B8" strokeWidth="11" />
      </SvgWrapper>
    );
  }

  if (level === 2) {
    return (
      <SvgWrapper
        viewBox="0 0 177 211"
        fill="none"
        {...props}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient
            id={ids.gradient}
            cx="50%"
            cy="50%"
            r="50%"
            fx="50%"
            fy="50%"
          >
            <stop offset="0%" stopColor="#EDE9FE" />
            <stop offset="100%" stopColor="#A78BFA" />
          </radialGradient>
        </defs>
        <path d={BOLT_PATH} fill={`url(#${ids.gradient})`} />
      </SvgWrapper>
    );
  }

  if (level === 3) {
    return (
      <SvgWrapper
        viewBox="0 0 177 211"
        fill="none"
        {...props}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id={ids.filter} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur
              stdDeviation="8"
              result="coloredBlur"
              in="SourceGraphic"
            />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d={BOLT_PATH}
          fill="#7C3AED"
          stroke="#C4B5FD"
          strokeWidth="3"
          filter={`url(#${ids.filter})`}
        />
      </SvgWrapper>
    );
  }

  if (level === 4) {
    return (
      <SvgWrapper
        viewBox="0 0 177 211"
        fill="none"
        {...props}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={ids.gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#EC4899" />
          </linearGradient>
          <filter id={ids.filter} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur
              stdDeviation="12"
              result="coloredBlur"
              in="SourceGraphic"
            />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d={BOLT_PATH}
          fill={`url(#${ids.gradient})`}
          filter={`url(#${ids.filter})`}
        />
      </SvgWrapper>
    );
  }

  if (level === 6) {
    // level 6: TurboBoost — dual-color gradient, max glow, fast shimmer sweep
    return (
      <SvgWrapper
        viewBox="0 0 177 211"
        fill="none"
        {...props}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={ids.gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#EC4899" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#06B6D4" />
          </linearGradient>
          <filter id={ids.filter} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur
              stdDeviation="20"
              result="coloredBlur"
              in="SourceGraphic"
            />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient
            id={ids.shimmer}
            gradientUnits="userSpaceOnUse"
            x1="-177"
            y1="0"
            x2="0"
            y2="0"
          >
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="50%" stopColor="white" stopOpacity="0.7" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
            <animate
              attributeName="x1"
              values="-177; 177; 177"
              keyTimes="0; 0.1; 1"
              dur="1.5s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="x2"
              values="0; 354; 354"
              keyTimes="0; 0.1; 1"
              dur="1.5s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
        <path
          d={BOLT_PATH}
          fill={`url(#${ids.gradient})`}
          filter={`url(#${ids.filter})`}
        />
        <path d={BOLT_PATH} fill={`url(#${ids.shimmer})`} />
      </SvgWrapper>
    );
  }

  // level === 5: level-4 look with stronger outer glow + shimmer sweep
  return (
    <SvgWrapper
      viewBox="0 0 177 211"
      fill="none"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={ids.gradient} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7C3AED" />
          <stop offset="50%" stopColor="#A855F7" />
          <stop offset="100%" stopColor="#EC4899" />
        </linearGradient>
        <filter id={ids.filter} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur
            stdDeviation="16"
            result="coloredBlur"
            in="SourceGraphic"
          />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient
          id={ids.shimmer}
          gradientUnits="userSpaceOnUse"
          x1="-177"
          y1="0"
          x2="0"
          y2="0"
        >
          <stop offset="0%" stopColor="white" stopOpacity="0" />
          <stop offset="50%" stopColor="white" stopOpacity="0.5" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
          <animate
            attributeName="x1"
            values="-177; 177; 177"
            keyTimes="0; 0.17; 1"
            dur="3s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="x2"
            values="0; 354; 354"
            keyTimes="0; 0.17; 1"
            dur="3s"
            repeatCount="indefinite"
          />
        </linearGradient>
      </defs>
      <path
        d={BOLT_PATH}
        fill={`url(#${ids.gradient})`}
        filter={`url(#${ids.filter})`}
      />
      <path d={BOLT_PATH} fill={`url(#${ids.shimmer})`} />
    </SvgWrapper>
  );
};
